import React, { useCallback, useState } from 'react';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { TrainingExerciseEntry, TrainingPlanPayload, TrainingExerciseKind } from '../../types';
import { NumericInput } from '../../components/NumericInput';
import { newId } from '../../lib/trainingPlanNormalize';

function defaultStrength(): TrainingExerciseEntry {
  return {
    entryId: newId(),
    kind: 'strength',
    name: '',
    setTargets: [{ setId: newId(), reps: 10, weightKg: 0 }],
  };
}

function defaultCardio(): TrainingExerciseEntry {
  return {
    entryId: newId(),
    kind: 'cardio',
    name: '',
    targetMinutes: 15,
  };
}

export interface TrainingPlanEditorProps {
  screenTitle: string;
  initialName: string;
  initialPayload: TrainingPlanPayload;
  onSave: (name: string, payload: TrainingPlanPayload) => Promise<void>;
  onBack: () => void;
  saving: boolean;
}

const TrainingPlanEditor: React.FC<TrainingPlanEditorProps> = ({
  screenTitle,
  initialName,
  initialPayload,
  onSave,
  onBack,
  saving,
}) => {
  const [name, setName] = useState(initialName);
  const [payload, setPayload] = useState<TrainingPlanPayload>(() =>
    JSON.parse(JSON.stringify(initialPayload))
  );
  const [dayIndex, setDayIndex] = useState(0);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const selectedDay = payload.days[dayIndex] ?? payload.days[0];

  const addDay = () => {
    const n = payload.days.length + 1;
    setPayload((p) => ({
      days: [...p.days, { dayId: newId(), title: `Day ${n}`, exercises: [] }],
    }));
    setDayIndex(n - 1);
  };

  const removeDay = (idx: number) => {
    setPayload((p) => {
      if (p.days.length <= 1) return p;
      const newDays = p.days.filter((_, i) => i !== idx);
      setDayIndex((di) => {
        if (idx < di) return di - 1;
        if (idx === di) return Math.max(0, di - 1);
        return Math.min(di, newDays.length - 1);
      });
      return { days: newDays };
    });
  };

  const updateDayTitle = (idx: number, title: string) => {
    setPayload((p) => ({
      days: p.days.map((d, i) => (i === idx ? { ...d, title } : d)),
    }));
  };

  const addExercise = (kind: TrainingExerciseKind) => {
    const ex = kind === 'strength' ? defaultStrength() : defaultCardio();
    setPayload((p) => ({
      days: p.days.map((d, i) =>
        i === dayIndex ? { ...d, exercises: [...d.exercises, ex] } : d
      ),
    }));
    setExpandedIdx(selectedDay.exercises.length);
  };

  const removeExercise = (idx: number) => {
    setPayload((p) => ({
      days: p.days.map((d, i) =>
        i === dayIndex ? { ...d, exercises: d.exercises.filter((_, j) => j !== idx) } : d
      ),
    }));
    if (expandedIdx === idx) setExpandedIdx(null);
  };

  const patchExercise = useCallback((idx: number, patch: Partial<TrainingExerciseEntry>) => {
    setPayload((p) => ({
      days: p.days.map((d, di) => {
        if (di !== dayIndex) return d;
        return {
          ...d,
          exercises: d.exercises.map((e, ei) => (ei === idx ? { ...e, ...patch } : e)),
        };
      }),
    }));
  }, [dayIndex]);

  const handleSave = async () => {
    if (!name.trim()) return;
    await onSave(name.trim(), payload);
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-theme-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-theme-primary" />
          </button>
          <h2 className="text-xl font-bold text-theme-primary">{screenTitle}</h2>
        </div>

        <input
          type="text"
          placeholder="Training plan name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-3 rounded-lg bg-theme-primary text-theme-primary border border-theme-primary text-sm font-medium focus:outline-none focus:ring-1 focus:ring-green-500 mb-4"
        />

        <div className="bg-theme-primary rounded-lg border border-theme-primary p-3 mb-4">
          <div className="flex flex-wrap gap-2 items-center mb-2">
            {payload.days.map((d, i) => (
              <button
                key={d.dayId}
                type="button"
                onClick={() => setDayIndex(i)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  i === dayIndex
                    ? 'bg-green-600 text-white'
                    : 'bg-theme-secondary text-theme-secondary hover:text-theme-primary'
                }`}
              >
                {d.title}
              </button>
            ))}
            <button
              type="button"
              onClick={addDay}
              className="px-3 py-1.5 rounded-lg text-sm border border-dashed border-theme-primary text-theme-secondary hover:text-green-400"
            >
              + Day
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={selectedDay.title}
              onChange={(e) => updateDayTitle(dayIndex, e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary"
            />
            {payload.days.length > 1 && (
              <button
                type="button"
                onClick={() => removeDay(dayIndex)}
                className="p-2 text-red-400 hover:text-red-300"
                title="Remove day"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-theme-secondary mb-2">
          Exercises for <span className="text-theme-primary font-medium">{selectedDay.title}</span>
        </p>

        <div className="space-y-2 mb-4">
          {selectedDay.exercises.map((entry, idx) => {
            const isExpanded = expandedIdx === idx;
            const targets = entry.kind === 'strength' ? entry.setTargets ?? [] : [];
            const summary =
              entry.kind === 'cardio'
                ? `${entry.targetMinutes ?? 0} min`
                : targets.length === 0
                  ? 'No sets'
                  : `${targets.length} set${targets.length !== 1 ? 's' : ''}: ${targets
                      .map((t) => `${t.reps}@${t.weightKg <= 0 ? 'BW' : `${t.weightKg}kg`}`)
                      .join(', ')}`;

            return (
              <div
                key={entry.entryId}
                className="bg-theme-primary rounded-lg border border-theme-primary overflow-hidden"
              >
                <div
                  className="flex items-center gap-2 px-3 py-3 cursor-pointer"
                  onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-theme-primary block truncate">
                      {entry.name.trim() || '(unnamed)'}
                    </span>
                    <span className="text-xs text-theme-secondary">
                      {entry.kind === 'cardio' ? 'Cardio' : 'Strength'} · {summary}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeExercise(idx);
                    }}
                    className="p-1.5 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-theme-secondary" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-theme-secondary" />
                  )}
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-theme-primary space-y-3">
                    <div>
                      <label className="text-xs text-theme-secondary block mb-1">Exercise name</label>
                      <input
                        type="text"
                        value={entry.name}
                        onChange={(e) => patchExercise(idx, { name: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary"
                      />
                    </div>
                    <div className="flex gap-2">
                      <span className="text-xs text-theme-secondary self-center">Type</span>
                      <select
                        value={entry.kind}
                        onChange={(e) => {
                          const k = e.target.value as TrainingExerciseKind;
                          if (k === 'cardio') {
                            patchExercise(idx, {
                              kind: 'cardio',
                              targetMinutes: entry.targetMinutes ?? 15,
                              setTargets: undefined,
                            });
                          } else {
                            patchExercise(idx, {
                              kind: 'strength',
                              setTargets:
                                entry.kind === 'strength' && entry.setTargets?.length
                                  ? entry.setTargets
                                  : [{ setId: newId(), reps: 10, weightKg: 0 }],
                              targetMinutes: undefined,
                            });
                          }
                        }}
                        className="flex-1 px-2 py-1.5 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary"
                      >
                        <option value="strength">Strength</option>
                        <option value="cardio">Cardio (minutes)</option>
                      </select>
                    </div>
                    {entry.kind === 'strength' ? (
                      <div className="space-y-2">
                        <p className="text-xs text-theme-secondary">
                          One row per set. Reps and kg can differ; use 0 kg for bodyweight.
                        </p>
                        {(entry.setTargets ?? []).map((row, si) => (
                          <div
                            key={row.setId}
                            className="flex flex-wrap gap-2 items-end p-2 rounded-lg border border-theme-primary bg-theme-secondary/20"
                          >
                            <span className="text-xs text-theme-secondary pb-2 w-11 shrink-0">
                              Set {si + 1}
                            </span>
                            <div className="w-24 shrink-0">
                              <label className="text-xs text-theme-secondary block mb-0.5">Reps</label>
                              <NumericInput
                                key={`${row.setId}-reps`}
                                value={row.reps}
                                min={1}
                                decimals={false}
                                onCommit={(reps) => {
                                  setPayload((p) => ({
                                    days: p.days.map((d, di) => {
                                      if (di !== dayIndex) return d;
                                      return {
                                        ...d,
                                        exercises: d.exercises.map((e, ei) => {
                                          if (ei !== idx || e.kind !== 'strength') return e;
                                          const st = (e.setTargets ?? []).map((s, j) =>
                                            j === si ? { ...s, reps } : s
                                          );
                                          return { ...e, setTargets: st };
                                        }),
                                      };
                                    }),
                                  }));
                                }}
                                className="w-full px-2 py-1.5 text-sm rounded bg-theme-secondary text-theme-primary border border-theme-primary"
                              />
                            </div>
                            <div className="w-24 shrink-0">
                              <label className="text-xs text-theme-secondary block mb-0.5">kg</label>
                              <NumericInput
                                key={`${row.setId}-kg`}
                                value={row.weightKg}
                                min={0}
                                decimals
                                onCommit={(weightKg) => {
                                  setPayload((p) => ({
                                    days: p.days.map((d, di) => {
                                      if (di !== dayIndex) return d;
                                      return {
                                        ...d,
                                        exercises: d.exercises.map((e, ei) => {
                                          if (ei !== idx || e.kind !== 'strength') return e;
                                          const st = (e.setTargets ?? []).map((s, j) =>
                                            j === si ? { ...s, weightKg } : s
                                          );
                                          return { ...e, setTargets: st };
                                        }),
                                      };
                                    }),
                                  }));
                                }}
                                className="w-full px-2 py-1.5 text-sm rounded bg-theme-secondary text-theme-primary border border-theme-primary"
                              />
                            </div>
                            <button
                              type="button"
                              title="Remove set"
                              disabled={(entry.setTargets?.length ?? 0) <= 1}
                              onClick={() => {
                                if ((entry.setTargets?.length ?? 0) <= 1) return;
                                setPayload((p) => ({
                                  days: p.days.map((d, di) => {
                                    if (di !== dayIndex) return d;
                                    return {
                                      ...d,
                                      exercises: d.exercises.map((e, ei) => {
                                        if (ei !== idx || e.kind !== 'strength') return e;
                                        const st = (e.setTargets ?? []).filter((_, j) => j !== si);
                                        return { ...e, setTargets: st };
                                      }),
                                    };
                                  }),
                                }));
                              }}
                              className="p-2 text-red-400 hover:text-red-300 disabled:opacity-30 mb-0.5"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setPayload((p) => ({
                              days: p.days.map((d, di) => {
                                if (di !== dayIndex) return d;
                                return {
                                  ...d,
                                  exercises: d.exercises.map((e, ei) => {
                                    if (ei !== idx || e.kind !== 'strength') return e;
                                    const st = [...(e.setTargets ?? [])];
                                    const last = st[st.length - 1];
                                    st.push({
                                      setId: newId(),
                                      reps: last?.reps ?? 10,
                                      weightKg: last?.weightKg ?? 0,
                                    });
                                    return { ...e, setTargets: st };
                                  }),
                                };
                              }),
                            }))
                          }
                          className="w-full py-2 text-sm rounded-lg border border-dashed border-theme-primary text-theme-secondary hover:text-green-400"
                        >
                          + Add set
                        </button>
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs text-theme-secondary block mb-1">Target minutes</label>
                        <NumericInput
                          value={entry.targetMinutes ?? 15}
                          min={1}
                          onCommit={(targetMinutes) => patchExercise(idx, { targetMinutes })}
                          className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => addExercise('strength')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-theme-primary text-theme-secondary hover:text-green-400 text-sm"
          >
            <Plus className="w-4 h-4" /> Strength
          </button>
          <button
            type="button"
            onClick={() => addExercise('cardio')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-theme-primary text-theme-secondary hover:text-green-400 text-sm"
          >
            <Plus className="w-4 h-4" /> Cardio
          </button>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save plan'}
        </button>

      </div>
    </div>
  );
};

export default TrainingPlanEditor;
